<template>
  <div v-if="locationName === '' || locationName == null || false">
    <div class="row">
      <div class="col-12">
        <h5>Dieser Kunde hat keinen Standort.</h5>
        <p>Sie müssen den Standort auswählen, wenn Sie Maßnahmen hinzufügen oder ändern möchten</p>
      </div>
    </div>
  </div>
  <div v-else>
    <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
      <div class="row">
        <div class="col-12">
          <h6 class="mb-4">Standort: {{ locationName }}</h6>

          <AuditInfo
              :user-role="currentUserRole"
              :created-by-user-data="formData.createdByUserData"
              :created-at="formData.createdAt"
              :updated-by-user-data="formData.updatedByUserData"
              :updated-at="formData.updatedAt"/>

          <div v-if="
              formData.unemployedId === '' ||
              formData.unemployedId == null ||
               false">
            <h6 class="mb-4" style="color: red">{{ $t('general.error') }}</h6>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-6 col-md-12 col-sm-12">
          <Datepicker
              v-model="formData.startDate"
              :required="true"
              label="Start"
              position="down"
              :validation="v$.startDate"
              :messages="v$.startDate.required.$message"/>

          <SelectVue v-model="formData.coach1Id"
                     :options="coach1Dropdown"
                     :required="true"
                     label="Coach 1"
                     placeholder="Auswählen Trainerin"
                     :validation="v$.coach1Id"
                     :messages="v$.coach1Id.required.$message"/>

          <SelectVue v-model="formData.clientId"
                     :options="clientDropdown"
                     :required="true"
                     label="Kostenträger"
                     placeholder="Wählen Sie Kostenträger"
                     :validation="v$.clientId"
                     :messages="v$.clientId.required.$message"/>

          <BaseNumber v-model="formData.cost"
                      type="number"
                      label="Umsatz pro Einheit"
                      :required=true
                      :isCurrency="true"
                      :validation="v$.cost"
                      :decimalPlaces="4"
                      :messages="v$.cost.required.$message"/>

          <div class="text-center">
            <BaseToggle
                v-model="formData.requiresSignature"
                label="Unterschriftspflichtig"/>
          </div>
        </div>
        <div class="col-lg-6 col-md-12 col-sm-12">
          <Datepicker
              v-model="formData.endDate"
              :required="true"
              label="Ende"
              position="down"
              :validation="v$.endDate"
              :messages="v$.endDate.required.$message"/>

          <SelectVue v-model="formData.coach2Id"
                     :options="coach2Dropdown"
                     :required="true"
                     label="Coach 1"
                     placeholder="Auswählen Trainerin"/>

          <BaseNumber v-model="formData.units"
                      type="number"
                      label="Einheiten"
                      :required=true
                      :validation="v$.units"
                      :messages="v$.units.required.$message"/>

          <BaseNumber v-model="formData.duration"
                      type="number"
                      label="Dauer in Wochen"
                      :required=true
                      :validation="v$.duration"
                      :messages="v$.duration.required.$message"/>

          <div class="text-center">
            <BaseToggle
                v-model="formData.digitalSignature"
                label="Digitale Unterschrift"/>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </div>
</template>

<script setup>
import caseManagementCoachingService from '@/api/Unemployed/CaseManagementCoaching';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import {
  getClientForCaseManagementDropdown,
  getUsersForCaseManagementCoachingDropdown
} from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaUserStore } from '@/store/piniaUserStore';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }          = useI18n();
const piniaUserState = piniaUserStore();
const currentUserRole = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const clientDropdown = ref([]);
const coach1Dropdown = ref([]);
const coach2Dropdown = ref([]);
const isLoaded       = ref(false);
const isSubmitting   = ref(false);
const emit           = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  unemployedId: {
    type: Number,
    required: true,
  },
  locationName: {
    type: String,
    required: true,
  },
  locationId: {
    type: Number,
    required: true,
  },
  caseManagementCoachingId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref({
  unemployedId: props.unemployedId, // !!! IMPORTANT
  clientId: null,
  coach1Id: null,
  coach2Id: null,
  startDate: '',
  endDate: '',
  duration: '',
  units: '',
  cost: '',
  requiresSignature: true,
  digitalSignature: true,
});

const formRules = ref({
  unemployedId: requiredRule(t),
  clientId: requiredRule(t),
  coach1Id: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  duration: requiredRule(t),
  units: requiredRule(t),
  cost: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.caseManagementCoachingId) {
      await caseManagementCoachingService.putCaseManagementCoaching(formData.value, +props.caseManagementCoachingId);
    } else {
      await caseManagementCoachingService.postCaseManagementCoaching(formData.value);
    }

    SuccessToast(t('general.toasts.save_success'));
    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function applyCaseManagementCoachingData(data) {
  try {
    const clientId = data.data?.client?.id;
    const coach1Id = data.data?.coach1?.id;
    const coach2Id = data.data?.coach2?.id;

    // Add coach/client to dropdown if is logically deleted on backend and is selected for coaching
    addCoachToDropdown(data.data?.coach1, coach1Dropdown);
    addCoachToDropdown(data.data?.coach2, coach2Dropdown);
    addClientToDropdown(data.data?.client, clientDropdown);

    formData.value = {
      ...data.data,
      unemployedId: props.unemployedId,
      clientId: clientId,
      coach1Id: coach1Id,
      coach2Id: coach2Id,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

function addClientToDropdown(clientData, dropdown) {
  if (clientData?.id) {
    const client = {
      label: clientData.name,
      value: clientData.id,
    };
    const exists = dropdown.value.some(item => item.value === client.value);
    if (!exists) {
      dropdown.value.push(client);
    }
  }
}

function addCoachToDropdown(coachData, dropdown) {
  if (coachData?.id) {
    const coach  = {
      label: `${coachData.firstName} ${coachData.lastName}`,
      value: coachData.id,
    };
    const exists = dropdown.value.some(item => item.value === coach.value);
    if (!exists) {
      dropdown.value.push(coach);
    }
  }
}

onMounted(async () => {
  CloseToast();

  const coachingDataPromise = props.caseManagementCoachingId
      ? caseManagementCoachingService.getCaseManagementCoaching(+props.caseManagementCoachingId)
      : null;

  await Promise.all([
    getUsersForCaseManagementCoachingDropdown(coach1Dropdown, props.locationId),
    getClientForCaseManagementDropdown(clientDropdown),
  ]);

  coach2Dropdown.value = [...coach1Dropdown.value];

  if (coachingDataPromise) {
    try {
      const {data} = await coachingDataPromise;
      await applyCaseManagementCoachingData(data);
    } catch (error) {
      await ErrorToast(error);
    }
  }

  isLoaded.value = true;
});


</script>
<style scoped>

</style>